import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import { AIRLINE_DIRECTORY, AIRLINE_CATEGORIES } from '../../data/airlines/directory';

// =====================================================================
// Transitions Hub — editorial index of all airline landing pages.
// Filter by category (low-cost / legacy) and search by airline name.
// =====================================================================

const FILTERS = [
  { id: 'all', label: 'All airlines' },
  { id: 'lowcost', label: AIRLINE_CATEGORIES.lowcost },
  { id: 'legacy', label: AIRLINE_CATEGORIES.legacy },
];

const TransitionsHubPage = () => {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  // SEO meta
  useEffect(() => {
    document.title = 'Airline Transitions to Private Aviation | Gate 33';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Cabin crew transitioning from commercial airlines to private aviation. Dedicated landing pages by airline — Air France, Lufthansa, Ryanair, KLM, Iberia and more.'
      );
    }
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return AIRLINE_DIRECTORY.filter((a) => {
      if (filter !== 'all' && a.category !== filter) return false;
      if (q && !a.name.toLowerCase().includes(q) && !a.country.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [filter, query]);

  const groups = useMemo(() => {
    const out = { lowcost: [], legacy: [] };
    for (const a of filtered) out[a.category]?.push(a);
    return out;
  }, [filtered]);

  return (
    <div data-testid="transitions-hub" className="min-h-screen">
      {/* HERO */}
      <section
        data-testid="transitions-hub-hero"
        className="relative bg-[#FDFBF7] section-padding"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="overline inline-block mb-6">Transitions</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
              From commercial cabin to private aviation.
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed">
              Dedicated landing pages for cabin crew transitioning from major European
              airlines to private aviation. Each page is built for the operational reality
              of the airline you fly with today, and the standards of the operators
              hiring tomorrow.
            </p>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section
        data-testid="transitions-hub-filters"
        className="bg-white border-y border-[#E5E0D8]"
      >
        <div className="container-custom py-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
            <div className="flex flex-wrap items-center gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`transitions-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`text-xs uppercase tracking-[0.18em] px-4 py-2 border transition-all duration-300 ${
                    filter === f.id
                      ? 'bg-[#0F0F0F] text-[#FDFBF7] border-[#0F0F0F]'
                      : 'bg-white text-[#1A1A1A] border-[#E5E0D8] hover:border-[#C5A880]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="flex-1 lg:flex lg:justify-end">
              <div className="relative inline-flex items-center w-full lg:w-80">
                <Search size={14} className="absolute left-3 text-[#9A9A9A]" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search airline or country…"
                  data-testid="transitions-search"
                  className="w-full bg-[#FDFBF7] border border-[#E5E0D8] pl-9 pr-4 py-2 text-sm text-[#1A1A1A] outline-none focus:border-[#C5A880] transition-colors"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AIRLINE GRID */}
      <section
        data-testid="transitions-hub-list"
        className="section-padding bg-white"
      >
        <div className="container-custom space-y-20">
          {(['lowcost', 'legacy']).map((cat) => {
            if (filter !== 'all' && filter !== cat) return null;
            const items = groups[cat];
            if (!items || items.length === 0) return null;
            return (
              <div key={cat} data-testid={`transitions-group-${cat}`}>
                <div className="flex items-baseline justify-between mb-10 pb-5 border-b border-[#E5E0D8]">
                  <h2 className="font-heading text-2xl sm:text-3xl tracking-tight">
                    {AIRLINE_CATEGORIES[cat]}
                  </h2>
                  <span className="overline">{items.length} airlines</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map((a) => (
                    <AirlineCard key={a.slug} airline={a} />
                  ))}
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-center py-20" data-testid="transitions-empty-state">
              <p className="text-[#5A5A5A]">No airline matches your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-dark py-12 md:py-16 border-b border-[#C5A880]/40">
        <div className="container-custom text-center max-w-3xl">
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-4">
            Your airline not listed?
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            We build the path with you anyway.
          </h2>
          <p className="text-[#9A9A9A] max-w-xl mx-auto mb-8">
            The Gate 33 Academy is built around the operational reality of private
            aviation — your starting airline only changes the entry point.
          </p>
          <Link
            to="/academy"
            data-testid="transitions-hub-final-cta"
            className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
          >
            See the 3-day Academy <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

const AirlineCard = ({ airline }) => {
  const isLive = airline.status === 'live';
  const href = isLive ? `/transition/${airline.slug}` : null;

  const inner = (
    <>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-2xl leading-none" aria-hidden="true">
          {airline.flag}
        </span>
        <h3 className="font-heading text-xl text-[#0F0F0F] leading-tight">
          {airline.name}
        </h3>
      </div>
      <p className="text-sm text-[#5A5A5A] mb-6">{airline.country}</p>

      <div className="flex items-center justify-between">
        <span className="overline text-[#C5A880]">
          {airline.languages.map((l) => l.toUpperCase()).join(' · ')}
        </span>
        {isLive ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#C5A880] uppercase tracking-wider">
            Open page <ArrowRight size={12} />
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A]">
            Coming soon
          </span>
        )}
      </div>
    </>
  );

  if (isLive) {
    return (
      <Link
        to={href}
        data-testid={`transitions-card-${airline.slug}`}
        className="group block bg-[#FDFBF7] border border-[#E5E0D8] p-7 hover:border-[#C5A880] hover:bg-white transition-all duration-300"
      >
        {inner}
      </Link>
    );
  }

  return (
    <div
      data-testid={`transitions-card-${airline.slug}`}
      className="block bg-[#FDFBF7] border border-[#E5E0D8] p-7 opacity-75"
    >
      {inner}
    </div>
  );
};

export default TransitionsHubPage;
